import { randomUUID } from "node:crypto";
import type { ComponentIR, OperationIR } from "@aaac/contracts";
import type { AgentLogWriter } from "./lib/agent-log-writer.js";
import type { MemoryRef, MemoryStore } from "./lib/memory-store.js";
import type { ObserverEmitFn } from "./lib/observability-bridge.js";

export interface OperationMemoryRefConfig {
  output?: boolean;
  input?: boolean;
  provider?: string;
  compat?: string;
}

type OperationWithMemoryRef = OperationIR & {
  memoryRef?: OperationMemoryRefConfig;
};

export interface MemoryStoreEntry {
  result: unknown;
  memoryRef: MemoryRef;
}

export interface AaacInvocationContext {
  runtime: AaacRuntime;
  policy?: AaacPolicy;
  observer?: AaacObserver;
  parentRunId?: string;
  abortSignal?: AbortSignal;
  memoryRef?: MemoryRef;
  memoryStore?: MemoryStore;
  logWriter?: AgentLogWriter;
  /** Populated when resuming from a previous memory_ref. */
  resumedState?: unknown;
  /** Current run ID for this invocation chain. */
  runId?: string;
}

export interface AaacRuntime {
  runWorkflow(
    workflowId: string,
    options: Record<string, unknown>,
    input: unknown,
    ctx: AaacInvocationContext,
  ): Promise<unknown>;
  runTask(
    taskId: string,
    options: Record<string, unknown>,
    input: unknown,
    ctx: AaacInvocationContext,
  ): Promise<unknown>;
}

export interface AaacPolicy {
  assertAllowed(operationName: string): void | Promise<void>;
}

export interface AaacObserver {
  onOperationStart?(operationName: string, input: unknown): void;
  onOperationEnd?(operationName: string, output: unknown): void;
  onOperationError?(operationName: string, error: Error): void;
  /**
   * Emit an agent-axis event to the observability pipeline.
   * Structurally matches EventCollector.emit() from @aaac/observability.
   * Pass an EventCollector instance to wire up the full pipeline.
   * When undefined, emission is a no-op (observer is optional).
   */
  emit?: ObserverEmitFn;
}

export type OperationHandler = (
  options: Record<string, unknown>,
  input: unknown,
  ctx: AaacInvocationContext,
) => Promise<unknown>;

export interface RuntimeBinding {
  execute(
    operationName: string,
    options: Record<string, unknown>,
    input: unknown,
    ctx: AaacInvocationContext,
  ): Promise<unknown>;
  getOperationNames(): string[];
  getOperationIR(name: string): OperationIR | undefined;
}

const DEFAULT_MEMORY_PROVIDER = "aaac-runtime";
const DEFAULT_MEMORY_COMPAT = "aaac/0.1";

function getMemoryRefConfig(
  op: OperationIR,
): OperationMemoryRefConfig | undefined {
  return (op as OperationWithMemoryRef).memoryRef;
}

function attachMemoryRef(result: unknown, memoryRef: MemoryRef): unknown {
  if (result !== null && typeof result === "object" && !Array.isArray(result)) {
    return { ...result, _memoryRef: memoryRef };
  }
  return { value: result, _memoryRef: memoryRef };
}

export function createRuntimeBinding(
  component: ComponentIR,
  handlers?: Record<string, OperationHandler>,
): RuntimeBinding {
  const operations = component.operations;

  return {
    getOperationNames(): string[] {
      return Object.keys(operations);
    },

    getOperationIR(name: string): OperationIR | undefined {
      return operations[name];
    },

    async execute(
      operationName: string,
      options: Record<string, unknown>,
      input: unknown,
      ctx: AaacInvocationContext,
    ): Promise<unknown> {
      const op = operations[operationName];
      if (!op) {
        throw new Error(`Unknown operation: ${operationName}`);
      }

      const memoryConfig = getMemoryRefConfig(op);
      const runId = ctx.runId ?? ctx.parentRunId ?? randomUUID();
      const execCtx: AaacInvocationContext = { ...ctx, runId };

      if (ctx.memoryRef && ctx.memoryStore) {
        const stored = await ctx.memoryStore.load(ctx.memoryRef);
        if (stored !== null) {
          const entry = stored as MemoryStoreEntry;
          execCtx.resumedState = entry.result ?? stored;
        }
      }

      await ctx.policy?.assertAllowed(operationName);
      ctx.observer?.onOperationStart?.(operationName, input);

      const startTime = Date.now();
      if (ctx.logWriter) {
        await ctx.logWriter.writeRunEvent(runId, {
          timestamp: new Date().toISOString(),
          type: "operation_start",
          runId,
          operationName,
          data: { input },
        });
      }

      try {
        const result = await dispatchOperation(
          operationName,
          op,
          options,
          input,
          execCtx,
          handlers,
        );

        let finalResult = result;
        let newMemoryRef: MemoryRef | undefined;

        if (memoryConfig?.output && ctx.memoryStore) {
          newMemoryRef = {
            id: randomUUID(),
            provider: memoryConfig.provider ?? DEFAULT_MEMORY_PROVIDER,
            compat: memoryConfig.compat ?? DEFAULT_MEMORY_COMPAT,
            created_at: new Date().toISOString(),
            parent_run_id: runId,
          };

          const entry: MemoryStoreEntry = {
            result,
            memoryRef: newMemoryRef,
          };
          await ctx.memoryStore.save(newMemoryRef, entry);
          finalResult = attachMemoryRef(result, newMemoryRef);
        }

        ctx.observer?.onOperationEnd?.(operationName, finalResult);

        if (ctx.logWriter) {
          await ctx.logWriter.writeRunEvent(runId, {
            timestamp: new Date().toISOString(),
            type: "operation_end",
            runId,
            operationName,
            durationMs: Date.now() - startTime,
            data: { output: finalResult },
          });
        }

        return finalResult;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        ctx.observer?.onOperationError?.(operationName, err);

        if (ctx.logWriter) {
          await ctx.logWriter.writeRunEvent(runId, {
            timestamp: new Date().toISOString(),
            type: "operation_error",
            runId,
            operationName,
            durationMs: Date.now() - startTime,
            error: err.message,
          });
        }

        throw err;
      }
    },
  };
}

async function dispatchOperation(
  operationName: string,
  op: OperationIR,
  options: Record<string, unknown>,
  input: unknown,
  ctx: AaacInvocationContext,
  handlers?: Record<string, OperationHandler>,
): Promise<unknown> {
  if (op.agentWorkflow) {
    return ctx.runtime.runWorkflow(op.agentWorkflow, options, input, ctx);
  }

  if (op.agentTask) {
    return ctx.runtime.runTask(op.agentTask, options, input, ctx);
  }

  if (op.handler) {
    const handler = handlers?.[operationName];
    if (!handler) {
      throw new Error(
        `No handler provided for deterministic operation "${operationName}"`,
      );
    }
    return handler(options, input, ctx);
  }

  throw new Error(`Operation "${operationName}" has no dispatch target`);
}
